'use client';

import React from 'react';
import type { QuestionConfig, AnswerData, ValidationRules } from '../types';
import { useTranslation } from '@/app/i18n/client';

interface TextInputQuestionProps {
  config: QuestionConfig;
  value: AnswerData;
  onChange: (value: AnswerData) => void;
  validationRules?: ValidationRules;
  error?: string;
  onEnterPress?: () => void; // Callback when Enter is pressed
  disabled?: boolean; // When true, lock the field (used by "ไม่แพ้" opt-out)
}

export default function TextInputQuestion({
  config,
  value,
  onChange,
  validationRules,
  error,
  onEnterPress,
  disabled = false,
}: TextInputQuestionProps) {
  const { lang } = useTranslation();

  // Helper function to get text in correct language
  const getText = (th: string | undefined, en: string | undefined): string => {
    if (lang === 'en') {
      return en || th || '';
    }
    return th || en || '';
  };

  const textValue = value?.value || '';
  const minLength = config?.minLength || validationRules?.minLength || 0;
  const maxLength = config?.maxLength || validationRules?.maxLength;
  const currentLength = textValue.length;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange({ value: e.target.value });
  };

  // DF #240: Trim leading/trailing whitespace so the stored answer matches the
  // rule's compare value. Done on BLUR, not on change — trimming per keystroke
  // would stop the user from typing a space between words.
  // The rule evaluator also trims both sides at comparison time (which is what
  // repairs already-saved padded rows); this keeps newly stored data clean.
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const trimmed = e.target.value.trim();

    if (trimmed !== e.target.value) {
      onChange({ value: trimmed });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !config.multiline && onEnterPress) {
      // Only trigger on Enter for single-line inputs (not textarea)
      e.preventDefault();
      onEnterPress();
    }
  };

  // Helper text to guide user
  const getHelperText = () => {
    if (currentLength === 0 && minLength > 0) {
      return getText(
        `กรุณากรอกข้อมูลอย่างน้อย ${minLength} ตัวอักษร`,
        `Please enter at least ${minLength} characters`
      );
    }
    if (minLength > 0 && currentLength > 0 && currentLength < minLength) {
      return getText(
        `กรุณากรอกข้อมูลอีก ${minLength - currentLength} ตัวอักษร`,
        `Please enter ${minLength - currentLength} more characters`
      );
    }
    return null;
  };

  const helperText = getHelperText();

  if (config.multiline) {
    return (
      <div className="space-y-2">
        <textarea
          value={textValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={getText(config.placeholder, config.placeholderEn)}
          rows={typeof config.rows === 'number' ? config.rows : 4}
          maxLength={maxLength}
          disabled={disabled}
          className={`input resize-none ${error ? 'input-error' : ''} ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
        />
        <div className="flex justify-between items-center">
          {helperText && (
            <p className="text-sm text-[#2A807B]">{helperText}</p>
          )}
          {maxLength && (
            <p className="text-sm text-gray-500 ml-auto">
              {currentLength} / {maxLength}
            </p>
          )}
        </div>
        {error && <p className="error-text">{error}</p>}
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm">
      <input
        type="text"
        value={textValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={getText(config.placeholder, config.placeholderEn)}
        maxLength={maxLength}
        disabled={disabled}
        className={`w-full px-6 pt-4 pb-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2A807B] focus:border-transparent transition-all ${
          error ? 'border-red-500' : 'border-gray-200'
        } ${disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
      />
      <div className="flex justify-between items-center mt-1">
        {helperText && (
          <p className="text-sm text-[#2A807B]">{helperText}</p>
        )}
        {maxLength && (
          <p className="text-sm text-gray-500 ml-auto">
            {currentLength} / {maxLength}
          </p>
        )}
      </div>
      {error && <p className="text-red-600 text-sm text-center">{error}</p>}
    </div>
  );
}
